import { memo, useEffect, useState } from 'react';
import type { NodeProps } from '@xyflow/react';
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Variable,
  Search,
  MessageSquare,
  Zap,
  Info,
  Copy,
} from 'lucide-react';

function InstructionNoteComponent(props: NodeProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const collapseVersion = (props.data as { collapseVersion?: string } | undefined)?.collapseVersion;

  useEffect(() => {
    setIsExpanded(false);
  }, [collapseVersion]);

  return (
    <div
      className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-900 dark:to-slate-800 border-2 border-amber-300 dark:border-amber-500/40 rounded-xl shadow-lg select-none"
      style={{ width: 340 }}
      data-quick-guide="instruction-note"
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center shadow-sm">
          <BookOpen className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-amber-900 dark:text-amber-200">📖 Guia Rápido</h3>
          <p className="text-[10px] text-amber-600 dark:text-amber-300">Variáveis e dicas do fluxo</p>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-amber-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-amber-400" />
        )}
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Divider */}
          <div className="border-t border-amber-200 dark:border-amber-500/30" />

          {/* Variables Section */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Variable className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-xs font-bold text-slate-700 dark:text-slate-100">Variáveis do Sistema</span>
            </div>
            <div className="bg-white/80 dark:bg-slate-900/80 rounded-lg border border-amber-200 dark:border-amber-500/30 p-2.5 space-y-1.5">
              <VarRow var_name="sender_name" desc="Nome do cliente no chat" />
              <VarRow var_name="sender_phone" desc="Telefone do cliente" />
              <VarRow var_name="message_text" desc="Última mensagem recebida" />
              <VarRow var_name="dialog_id" desc="ID do diálogo Bitrix24" />
              <VarRow var_name="userId" desc="ID do usuário no Bitrix" />
              <VarRow var_name="chatId" desc="ID do chat" />
            </div>
          </div>

          {/* CRM Variables */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Search className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-xs font-bold text-slate-700 dark:text-slate-100">Após "Buscar Cliente"</span>
            </div>
            <div className="bg-white/80 dark:bg-slate-900/80 rounded-lg border border-amber-200 dark:border-amber-500/30 p-2.5 space-y-1.5">
              <VarRow var_name="crm_entity_type" desc="CONTACT, COMPANY ou DEAL" />
              <VarRow var_name="crm_entity_id" desc="ID da entidade encontrada" />
              <VarRow var_name="found_entity_id" desc="ID principal encontrado no Buscar Cliente" />
              <VarRow var_name="found_contact_id" desc="ID do contato encontrado (quando houver)" />
              <VarRow var_name="found_company_id" desc="ID da empresa encontrada (quando houver)" />
              <VarRow var_name="found_deal_id" desc="ID do negócio encontrado (quando houver)" />
              <VarRow var_name="crm_entity_name" desc="Nome/Título da entidade encontrada" />
              <VarRow var_name="contact.FIELD" desc="Campos do contato (NAME, PHONE...)" />
            </div>
          </div>

          {/* How to use */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <MessageSquare className="w-3.5 h-3.5 text-violet-500" />
              <span className="text-xs font-bold text-slate-700 dark:text-slate-100">Como Usar Variáveis</span>
            </div>
            <div className="bg-white/80 dark:bg-slate-900/80 rounded-lg border border-amber-200 dark:border-amber-500/30 p-2.5 text-[11px] text-slate-600 dark:text-slate-300 space-y-1.5">
              <p>
                Use <code className="bg-amber-100 dark:bg-amber-500/20 px-1 rounded text-amber-800 dark:text-amber-200 font-mono text-[10px]">{'{{nome_variavel}}'}</code> em qualquer mensagem.
              </p>
              <div className="bg-slate-50 dark:bg-slate-800 rounded p-2 font-mono text-[10px] text-slate-500 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                Olá <span className="text-blue-600 dark:text-blue-300">{'{{sender_name}}'}</span>! Seu telefone é <span className="text-blue-600 dark:text-blue-300">{'{{sender_phone}}'}</span>?
              </div>
              <p className="text-[10px] text-amber-600 dark:text-amber-300 mt-1">
                💡 As variáveis são preenchidas automaticamente com os dados do Bitrix24.
              </p>
            </div>
          </div>

          {/* Tips */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Zap className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-bold text-slate-700 dark:text-slate-100">Dicas</span>
            </div>
            <div className="bg-white/80 dark:bg-slate-900/80 rounded-lg border border-amber-200 dark:border-amber-500/30 p-2.5 text-[11px] text-slate-600 dark:text-slate-300 space-y-2">
              <Tip text="O nó Buscar Cliente procura no CRM pelo telefone. Conecte as saídas: 'Encontrado' e 'Não Encontrado'." />
              <Tip text="Variáveis criadas com 'Definir Variável' ou 'Aguardar Entrada' ficam disponíveis nos próximos nós; no modo Conversa livre, também permanecem salvas na mesma conversa (sessão) entre execuções, até alteração ou reinício da sessão." />
              <Tip text="Na Pergunta (botões), o usuário pode responder pelo número (1, 2, 3) ou pelo texto da opção." />
              <Tip text="Use 'Condição' para criar caminhos diferentes baseados nas respostas do usuário." />
              <Tip text="Blocos usam tamanho padrão para manter o fluxo organizado, mesmo com textos longos." />
              <Tip text="Para copiar vários blocos: segure Ctrl (ou Cmd no Mac), selecione os blocos e use Ctrl+C / Ctrl+V." />
            </div>
          </div>

          {/* Copy shortcuts */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Copy className="w-3.5 h-3.5 text-sky-500" />
              <span className="text-xs font-bold text-slate-700 dark:text-slate-100">Copiar Blocos</span>
            </div>
            <div className="bg-white/80 dark:bg-slate-900/80 rounded-lg border border-amber-200 dark:border-amber-500/30 p-2.5 text-[11px] text-slate-600 dark:text-slate-300 space-y-1.5">
              <p>Selecione um bloco e clique no ícone <strong>Copiar</strong> (ao lado do ícone de excluir).</p>
              <p>Para copiar em lote, selecione mais de um bloco e use <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded font-mono text-[10px]">Ctrl + C</code> e <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded font-mono text-[10px]">Ctrl + V</code> (ou <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded font-mono text-[10px]">Cmd + C</code> / <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded font-mono text-[10px]">Cmd + V</code> no Mac).</p>
              <p>Atalho rápido: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded font-mono text-[10px]">Ctrl + D</code> (Windows/Linux) ou <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded font-mono text-[10px]">Cmd + D</code> (Mac).</p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">A cópia mantém a mesma configuração e aparece com pequeno deslocamento para facilitar a edição.</p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-start gap-1.5 text-[10px] text-amber-500 dark:text-amber-300 pt-1">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Este bloco é apenas informativo — não afeta o fluxo. Você pode arrastá-lo para qualquer posição.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function VarRow({ var_name, desc }: { var_name: string; desc: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <code className="text-[10px] font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded whitespace-nowrap">
        {`{{${var_name}}}`}
      </code>
      <span className="text-[10px] text-slate-500 dark:text-slate-300 text-right">{desc}</span>
    </div>
  );
}

function Tip({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-1.5">
      <span className="text-amber-400 leading-none mt-0.5">•</span>
      <span>{text}</span>
    </div>
  );
}

export const InstructionNote = memo(InstructionNoteComponent);
